import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import DoneIcon from "@material-ui/icons/Done";
import { Capacitor } from "@capacitor/core";
import AddButton from "components/common/AddButton";

import ReportOverviewCard from "../../components/ReportOverviewCard";
import ItemDetailBottomSheet from "../../components/ItemDetailBottomSheet";

import {
  useReportState,
  useReportDispatch,
  updateItemGroups,
  removeItem
} from "../../state";
import { useGPSLocation } from "providers/LocationProvider";
import { ReportItem, ItemGroup } from "pages/photo/types";
import { LatLong } from "types/GPSLocation";
import { LitterFunction } from "custom/litterFunctions";
import {
  linkToReportUpload,
  linkToReportFunctionSelect
} from "routes/report/links";

import {
  usePhotoPageState,
  usePhotoPageDispatch,
  setMetaData
} from "pages/photo/state";
import {
  isBrowserImageState,
  isCordovaImageState,
  isImageMetaState
} from "pages/photo/state/types";
import loadPhoto from "pages/photo/pages/CategorisePhotoPage/utils";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box"
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2)
  },
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[200]}`
  },
  itemCount: {
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#495459",
    marginBottom: theme.spacing(1)
  }
}));

function isLatLong(value: any): value is LatLong {
  return (
    !!value &&
    typeof value.latitude === "number" &&
    typeof value.longitude === "number"
  );
}

export default function ReportOverviewPage() {
  const classes = useStyles();
  const history = useHistory();
  const state = useReportState();
  const dispatch = useReportDispatch();
  const photoState = usePhotoPageState();
  const photoDispatch = usePhotoPageDispatch();
  const gpsLocation = useGPSLocation();

  const [selectedItem, setSelectedItem] = useState<{
    fn: LitterFunction;
    item: ReportItem;
  } | null>(null);

  useEffect(() => {
    if (isImageMetaState(photoState)) {
      return;
    }

    const photoStateLocation = (photoState as any).imgLocation;
    const existingLocation: LatLong | null = isLatLong(photoStateLocation)
      ? photoStateLocation
      : gpsLocation
      ? {
          latitude: gpsLocation.latitude,
          longitude: gpsLocation.longitude
        }
      : null;

    if (isCordovaImageState(photoState)) {
      const { file, fromCamera } = photoState;
      loadPhoto({
        fileOrFileName: Capacitor.convertFileSrc(file.filename),
        fromCamera,
        gpsLocation,
        cordovaMetadata: file.json_metadata
          ? JSON.parse(file.json_metadata)
          : null,
        callback: (metadata) => {
          photoDispatch(
            setMetaData({
              ...metadata,
              imgLocation: existingLocation || metadata.imgLocation
            })
          );
        }
      });
      return;
    }

    if (isBrowserImageState(photoState)) {
      const { file, fromCamera } = photoState;
      loadPhoto({
        fileOrFileName: file,
        fromCamera,
        gpsLocation,
        callback: (metadata) => {
          photoDispatch(
            setMetaData({
              ...metadata,
              imgLocation: existingLocation || metadata.imgLocation
            })
          );
        }
      });
    }
  }, [photoDispatch, photoState, gpsLocation]);

  // Flatten all items across functions
  const allItems: Array<{ fn: LitterFunction; item: ReportItem }> = [];
  for (const [fn, items] of Object.entries(state.items)) {
    for (const item of items) {
      if (item.totalQuantity > 0) {
        allItems.push({ fn: fn as LitterFunction, item });
      }
    }
  }

  const totalItems = allItems.reduce(
    (sum, { item }) => sum + item.totalQuantity,
    0
  );

  const handleSaveGroups = (groups: ItemGroup[]) => {
    if (selectedItem) {
      dispatch(
        updateItemGroups(
          selectedItem.fn,
          selectedItem.item.category.id!,
          groups
        )
      );
      setSelectedItem(null);
    }
  };

  const handleDeleteItem = () => {
    if (selectedItem) {
      dispatch(removeItem(selectedItem.fn, selectedItem.item.category.id!));
      setSelectedItem(null);
    }
  };

  const handleSubmit = () => {
    const imgSrc = isImageMetaState(photoState) ? photoState.imgSrc : "";
    const imgLocation = isImageMetaState(photoState)
      ? photoState.imgLocation
      : null;

    if (!imgSrc) {
      console.warn(
        "[ReportOverviewPage] No imgSrc found in photo state. The upload page will redirect back to overview.",
        {
          photoState
        }
      );
    }

    // Navigate to upload — this will be handled by the upload route
    history.push({
      pathname: linkToReportUpload(),
      state: { items: allItems, imgSrc, imgLocation }
    });
  };

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.list}>
          {allItems.map(({ fn, item }) => (
            <ReportOverviewCard
              key={`${fn}-${item.category.id}`}
              reportItem={item}
              onClick={() => setSelectedItem({ fn, item })}
            />
          ))}
          <AddButton onClick={() => history.push(linkToReportFunctionSelect())}>
            Add from another category
          </AddButton>
        </div>
        <div className={classes.footer}>
          <Typography className={classes.itemCount}>
            {totalItems} items in this report
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disableElevation
            disabled={totalItems === 0 || !isImageMetaState(photoState)}
            onClick={handleSubmit}
            startIcon={<DoneIcon />}
          >
            Submit report
          </Button>
        </div>
      </div>

      <ItemDetailBottomSheet
        open={!!selectedItem}
        reportItem={selectedItem?.item ?? null}
        onClose={() => setSelectedItem(null)}
        onSave={handleSaveGroups}
        onDelete={handleDeleteItem}
      />
    </>
  );
}
